import {
  useRef,
  useEffect,
  useLayoutEffect,
  useState,
  forwardRef,
} from "react";
import { CircleCheck, CircleX } from "lucide-react";

/**
 * 원티드 작성 > 날짜별 사유 입력 말풍선 컴포넌트
 *
 * @description
 * - 저장된 사유가 있으면 readonly 모드로 표시 (삭제 버튼만)
 * - 새로 입력하는 경우 편집 모드 (저장 버튼만)
 * - ESC 키로 닫기 가능
 */

interface CommentInputProps {
  commentValue: string;
  onCommentChange: (value: string) => void;
  hasSavedComment: boolean; // 서버에 저장된 사유가 있는지 여부
  onSave: () => void;
  onDelete?: () => void;
  onClose: () => void;
  onEdit?: () => void;
  className?: string;
}

const CommentInput = forwardRef<HTMLTextAreaElement, CommentInputProps>(
  (
    {
      commentValue,
      onCommentChange,
      hasSavedComment,
      onSave,
      onDelete,
      onClose,
      onEdit,
      className,
    },
    ref
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [isFocused, setIsFocused] = useState(false);
    const [isComposing, setIsComposing] = useState(false); // 한글 조합 중인지 체크

    useLayoutEffect(() => {
      if (!hasSavedComment && textareaRef.current) {
        textareaRef.current.focus();
        const len = textareaRef.current.value.length;
        textareaRef.current.setSelectionRange(len, len);
      }
    }, [hasSavedComment]);

    useEffect(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const adjustHeight = () => {
        textarea.style.height = "auto";
        if (textarea.scrollHeight > 0) {
          textarea.style.height = `${textarea.scrollHeight}px`;
        }
      };

      adjustHeight();

      const observer = new ResizeObserver(() => {
        if (textarea.scrollHeight > textarea.clientHeight) {
          textarea.style.height = `${textarea.scrollHeight}px`;
        }
      });

      observer.observe(textarea);

      return () => observer.disconnect();
    }, [commentValue]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Escape") {
        onClose();
      }
      if (e.nativeEvent.isComposing) return;

      if (e.key === "Enter" && !e.shiftKey && !isComposing) {
        e.preventDefault();
        onSave();
        onClose();
        setIsFocused(false);
        textareaRef.current?.blur();
      }
    };

    const renderButtons = () => {
      if (hasSavedComment && !isFocused) {
        return (
          onDelete && (
            <button
              onClick={onDelete}
              onMouseDown={(e) => e.preventDefault()}
              className="flex-1 text-xs font-semibold text-[#C2C2C2] flex justify-center"
            >
              <CircleX size={17} className="text-[#C2C2C2]" />
            </button>
          )
        );
      }

      return (
        <button
          onClick={() => {
            onSave();
            onClose();
            setIsFocused(false);
            textareaRef.current?.blur();
          }}
          onMouseDown={(e) => e.preventDefault()}
          className="flex-1 text-xs font-semibold text-[#C2C2C2] flex justify-center"
        >
          <CircleCheck size={17} className="text-[#C2C2C2]" />
        </button>
      );
    };

    return (
      <div
        // className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-white border-1 border-[#D6D6DE] rounded-[5px] p-3 shadow-lg z-[1000] min-w-[200px] max-w-[280px]"
        className={`flex items-center justify-between absolute bottom-full left-1/2 -translate-x-1/2 mb-2 border-1 border-[#D6D6DE] bg-white rounded-[7px] py-[7px] px-[7px] shadow-lg z-[1000] min-w-[150px] max-w-[280px] ${className || ""}`}
        onClick={(e) => {
          e.stopPropagation();
          onEdit?.();
        }}
      >
        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[8px] border-t-[#D6D6DE]" />
        <div className="absolute -bottom-[6px] left-1/2 -translate-x-1/2 w-0 h-0 border-l-[7px] border-l-transparent border-r-[7px] border-r-transparent border-t-[7px] border-t-white" />

        <textarea
          ref={textareaRef}
          className="leading-[15px] py-0 w-full px-[2px] text-[16px] font-pretendard font-semibold !text-text-main2 placeholder:text-text-main2 bg-transparent resize-none overflow-hidden focus:outline-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
          placeholder="사유를 작성하세요."
          value={commentValue}
          onChange={(e) => onCommentChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onCompositionStart={() => setIsComposing(true)} // 한글 조합 시작
          onCompositionEnd={() => setIsComposing(false)} // 한글 조합 완료
          onFocus={() => {
            setIsFocused(true);
            onEdit?.();
          }}
          onBlur={() => setIsFocused(false)}
          maxLength={30}
          rows={1}
        />
        <div className="flex h-[-webkit-fill-available] items-start">
          {renderButtons()}
        </div>
      </div>
      // <div
      //     className={`flex items-center justify-between absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-[#333333B2] rounded-[7px] py-[7px] px-[7px] shadow-lg z-[1000] min-w-[150px] max-w-[280px] ${className || ""}`}
      //     onClick={(e) => {
      //         e.stopPropagation();
      //         onEdit?.();
      //     }}
      // >
      //     <div
      //         className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[8px] border-t-[#333333B2]"
      //         aria-hidden="true"
      //     />

      //     <textarea
      //         ref={textareaRef}
      //         className="leading-[15px] py-0 w-full px-[2px] text-[16px] font-pretendard font-semibold !text-white placeholder:text-white bg-transparent resize-none overflow-hidden focus:outline-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
      //         placeholder="사유를 작성하세요."
      //         value={commentValue}
      //         onChange={(e) => onCommentChange(e.target.value)}
      //         onKeyDown={handleKeyDown}
      //         onFocus={() => {
      //             setIsFocused(true);
      //             onEdit?.();
      //         }}
      //         onBlur={() => setIsFocused(false)}
      //         maxLength={30}
      //         rows={1}
      //     />
      //     <div className="flex h-[-webkit-fill-available] items-start">
      //         {renderButtons()}
      //     </div>
      // </div>
    );
  }
);

CommentInput.displayName = "CommentInput";

export default CommentInput;
